/**
 * The words a notification shows, in the reader's language (COL-528).
 *
 * buildOwnerNotificationFeed used to build German sentences inline, so the
 * portal's EN switch moved every label around them and left the feed itself in
 * German. The builder now returns a `kind` and the few values that vary; this
 * turns those into a title and a one-line description through `t()`.
 *
 * SEPARATE FROM THE BUILDER on purpose. The builder is pure and has no access
 * to a translator — it runs over rows and decides WHICH events exist and in
 * what order, which is testable without a language. This decides how one of
 * them reads, which is not.
 *
 * The money and the dates are formatted for the reader's locale too: a German
 * owner reading "1,800.00" would be reading a different number.
 */
import type {
  OwnerNotificationEntry,
  OwnerNotificationKind,
} from "./owner-notification-feed";

export type Translate = (key: string, fallback?: string) => string;

/**
 * Fill `{name}` placeholders. split/join rather than replaceAll, which this
 * project's lib target does not have, and rather than a RegExp, which would
 * need the value escaped — a property called "WE 1 (hinten)" is a legal label
 * and would otherwise be read as a group.
 */
function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (out, [k, v]) => out.split(`{${k}}`).join(v),
    template
  );
}

function money(eur: number | null | undefined, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(eur ?? 0);
}

function day(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Title and description for one entry.
 *
 * The German fallbacks are the exact strings the feed used to hard-code, so a
 * database with no `translations` rows yet renders precisely what it rendered
 * before — this change adds English, it does not reword German.
 */
export function ownerNotificationCopy(
  entry: OwnerNotificationEntry,
  t: Translate,
  locale: string
): { title: string; desc: string } {
  const p = entry.params;
  const property = entry.property;
  const tenant = p.tenant ?? t("owner.notif.tenantFallback", "Mieter");
  const room = p.room ? ` · ${t("owner.notif.room", "Zimmer")} ${p.room}` : "";

  const key = (suffix: string) => `owner.notif.${entry.kind}.${suffix}`;

  const copy: Record<OwnerNotificationKind, { title: string; desc: string }> = {
    portal_ready: {
      title: t(key("title"), "Owner Portal ist bereit"),
      desc: t(
        key("desc"),
        "Ihr Onboarding ist abgeschlossen und Ihr Portal steht ab sofort bereit."
      ),
    },
    lease_signed: {
      title: fill(
        t(key("title"), "Mietvertrag für {property} unterschrieben"),
        { property }
      ),
      desc: fill(
        t(
          key("desc"),
          "Ein neuer Mietvertrag wurde hinterlegt. Mieter: {tenant}{room}."
        ),
        { tenant, room }
      ),
    },
    deposit_received: {
      title: fill(t(key("title"), "Kaution für {property} eingegangen"), {
        property,
      }),
      desc: fill(
        t(
          key("desc"),
          "Die Kaution wurde erfolgreich eingezahlt. Betrag: {amount} · Mieter: {tenant}."
        ),
        { amount: money(p.amountEur, locale), tenant }
      ),
    },
    keys_handed_over: {
      title: fill(t(key("title"), "Schlüsselübergabe für {property} erfolgt"), {
        property,
      }),
      desc: fill(
        t(key("desc"), "Die Schlüsselübergabe an {tenant} wurde durchgeführt."),
        {
          tenant,
        }
      ),
    },
    move_in_protocol: {
      title: fill(
        t(key("title"), "Übergabeprotokoll für {property} eingereicht"),
        { property }
      ),
      desc: t(
        key("desc"),
        "Das Einzugsprotokoll wurde hinterlegt und steht in Ihrem Owner Portal bereit."
      ),
    },
    rent_received: {
      title: fill(t(key("title"), "Miete für {property} erhalten"), {
        property,
      }),
      desc: fill(
        t(
          key("desc"),
          "Die monatliche Miete wurde erfolgreich eingezahlt. Mieter: {tenant} · Betrag: {amount}."
        ),
        { tenant, amount: money(p.amountEur, locale) }
      ),
    },
    move_out_date_confirmed: {
      title: fill(t(key("title"), "Auszugstermin für {property} bestätigt"), {
        property,
      }),
      desc: fill(
        t(key("desc"), "{tenant} hat den Auszug zum {date} bestätigt."),
        {
          tenant,
          date: day(p.date, locale),
        }
      ),
    },
    move_out_protocol: {
      title: fill(
        t(key("title"), "Auszugsprotokoll für {property} verfügbar"),
        { property }
      ),
      desc: t(
        key("desc"),
        "Das Protokoll steht in Ihrem Portal bereit. Die Kautionsprüfung läuft."
      ),
    },
    keys_returned: {
      title: fill(t(key("title"), "Schlüsselrückgabe für {property} erfolgt"), {
        property,
      }),
      desc: fill(t(key("desc"), "{tenant} hat die Schlüssel zurückgegeben."), {
        tenant,
      }),
    },
  };

  return copy[entry.kind];
}
